import React, {ButtonHTMLAttributes, MouseEventHandler} from 'react'
import styled, {css, DefaultTheme, keyframes} from 'styled-components'

import type {MarginProps, StyleProps, ResponsiveValue} from '../../'
import convertLayoutProps from '../../utils/convertLayoutProps'
import FocusTrap from '../focus-trap/FocusTrap'
import Box from '../layout/Box'
import {useTemplateOpener} from '../template-opener/TemplateOpener'

export interface ButtonProps extends MarginProps {
  id?: string
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type']
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  children: React.ReactNode
  href?: string
  target?: string
  rel?: string
  className?: string
  $styles?: StyleProps
  $size?: keyof DefaultTheme['buttons']['sizes']
  $buttonType?: ResponsiveValue<keyof DefaultTheme['buttons']['variants']>
  $withArrow?: boolean
  isLoading?: boolean
  templateId?: string
  onFocus?: React.FocusEventHandler<HTMLAnchorElement | HTMLButtonElement>
  onBlur?: React.FocusEventHandler<HTMLAnchorElement | HTMLButtonElement>
  disabled?: boolean
}

interface SpinnerProps {
  $size?: number
  $gutter?: 4
  className?: string
}

const SpinnerBase = ({$size = 24, $gutter = 4, className}: SpinnerProps): React.ReactElement => (
  <Box className={className} $styles={{width: $size, height: $size}}>
    <svg viewBox={`0 0 ${$size} ${$size}`}>
      <title>Loading...</title>
      <circle cx={$size / 2} cy={$size / 2} r={$size / 2 - $gutter / 2} />
    </svg>
  </Box>
)

const spinner = (size: number) => keyframes`
  0% {
    stroke-dashoffset: ${0.66 * size};
    transform: rotate(0deg);
  } 50% {
    stroke-dashoffset: ${3.14 * size};
    transform: rotate(720deg);
  } 100% {
    stroke-dashoffset: ${0.66 * size};
    transform: rotate(1080deg);
  }
`

const RotatingSpinner = styled(SpinnerBase)<SpinnerProps>`
  svg {
    x: 0px;
    y: 0px;

    ${({theme, $size = 24}) => css`
      ${theme.normalizeStyles({
        width: $size,
        height: $size,
      })}

      circle {
        fill: transparent;
        stroke: #ffffff;
        stroke-width: 2;
        stroke-linecap: round;
        transform-origin: center;
        animation: ${spinner($size)} 2s linear infinite;
        stroke-dasharray: ${3.14 * $size};
      }
    `}
  }
`
const Hidden = styled.div`
  visibility: hidden;
`

//! missing rel, event_class, target from twig

const ButtonElement = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  (
    {isLoading = false, children, href, onClick, templateId, className, disabled, ...props},
    ref,
  ) => {
    const {setTemplateId} = useTemplateOpener()

    if (isLoading) {
      return (
        <button
          className={className}
          disabled={true}
          ref={ref as React.Ref<HTMLButtonElement>}
          {...props}
        >
          <FocusTrap>
            <RotatingSpinner />
            <Hidden>{children}</Hidden>
          </FocusTrap>
        </button>
      )
    }

    if (href) {
      return (
        <a
          onClick={e => {
            if (disabled) {
              e.preventDefault()
              return
            }
            if (templateId) {
              e.preventDefault()
              setTemplateId(templateId)
            }

            if (onClick) {
              onClick(e)
            }
          }}
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={`${className} js__event-link`}
          {...props}
        >
          <FocusTrap>{children}</FocusTrap>
        </a>
      )
    }

    return (
      <button
        onClick={e => {
          if (templateId) {
            e.preventDefault()
            setTemplateId(templateId)
          }

          if (onClick) {
            onClick(e)
          }
        }}
        ref={ref as React.Ref<HTMLButtonElement>}
        className={className}
        disabled={disabled}
        {...props}
      >
        <FocusTrap>{children}</FocusTrap>
      </button>
    )
  },
)

ButtonElement.displayName = 'ButtonElement'

const ButtonBase = styled(ButtonElement)`
  display: inline-block;
  padding: 0;
  transition: background ${({theme}) => theme.animation.transitionDuration},
    border-color ${({theme}) => theme.animation.transitionDuration};
  font-family: ${({theme}) => theme.fonts.formular};
  text-decoration: none;
  appearance: none;
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  white-space: nowrap;
`

const Button = styled(ButtonBase)<ButtonProps>`
  ${({theme, $buttonType}) =>
    $buttonType && theme.expandVariants($buttonType, theme.buttons.variants)}

  ${({theme, $styles, $withArrow, $buttonType, $size, ...props}) => {
    const buttonSize = $size ? theme.buttons.sizes[$size] : {}

    return theme.normalizeStyles({
      ...buttonSize,
      ...convertLayoutProps(props),
      ...$styles,
    })
  }}

  ${FocusTrap} {
    position: relative;

    ${({theme, $size, $withArrow}) => {
      let buttonSpacing = {}

      if ($size) {
        buttonSpacing = $withArrow
          ? theme.buttons.withArrowSpacing[$size]
          : theme.buttons.spacing[$size]
      }

      return theme.normalizeStyles(buttonSpacing)
    }}
  }

  ${RotatingSpinner} {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  ${props =>
    props.$withArrow &&
    css`
      ${FocusTrap}:after {
        content: ' \\2192';
      }
    `}
`

export default Button
